import type { SxProps } from '@mui/material';

import {
  Box,
  Checkbox as HephaestusCheckbox,
  Typography,
  type TypographyVariant,
  useTheme,
} from 'hephaestus/web';
import { useEffect, useState } from 'react';
import { useFieldIds } from '../fieldLabel/fieldLabel';
import { HelpText } from '../helpText/helpText';
import { useHelpTextTokens } from '../helpText/helpText.tokens';
import InfoDialog from '../infoDialog/infoDialog';

type CheckboxFieldFunctionalProps = {
  id?: string;
  name?: string;
  checked?: boolean;
  indeterminate?: boolean;
  onChange?: (onoff: boolean) => void;
  onClick?: (e: any) => void;
  disabled?: boolean;
  infoTooltip?: string;
  'data-testid'?: string;
};
type CheckboxFieldVisualProps = {
  error?: boolean;
  label?: string;
  size?: 'medium' | 'small';
  chip?: boolean;
  customStyle?: SxProps;
  checkBoxStyle?: SxProps;
  labelVariant?: TypographyVariant;
  centerInfoToolTip?: boolean;
};

export const CheckboxField = (
  props: CheckboxFieldFunctionalProps & CheckboxFieldVisualProps,
) => {
  const {
    checked = false,
    indeterminate = false,
    onChange,
    disabled = false,
    label,
    size = 'medium',
    chip = false,
    customStyle,
    checkBoxStyle,
    infoTooltip,
    labelVariant,
    centerInfoToolTip,
    onClick,
    error,
    id,
    name,
    'data-testid': dataTestId = 'checkbox',
  } = props;
  const { inputId } = useFieldIds(id);
  const theme = useTheme();
  const helpTextTokens = useHelpTextTokens(theme);
  const [on, setOn] = useState<boolean>(checked);
  const variant =
    labelVariant || (size === 'small' ? 'bodySmall' : 'bodyLarge');
  const labelColor =
    theme.component?.checkbox?.defaultColor ?? theme.color.onSurface;
  const disabledLabelColor =
    theme.component?.checkbox?.disabledColor ?? theme.color.onDisabled;

  const shouldDisplayInDialog = helpTextTokens.displayInDialog;

  useEffect(() => {
    if (checked !== undefined) setOn(checked);
  }, [checked]);

  const setOnAndPropagate = (newOn: boolean) => {
    setOn(newOn);
    if (onChange) onChange(newOn);
  };

  return (
    <Box
      sx={
        {
          display: 'flex',
          flexDirection: 'column',
          ...customStyle,
        } as any
      }
    >
      <Box
        sx={
          {
            display: 'flex',
            flexDirection: 'row',

            background: chip
              ? checked
                ? theme.color.supportOneVariant
                : theme.color.surfaceContainerVariant
              : 'transparent',

            padding: chip ? '4px 8px' : undefined,
            width: chip ? 'fit-content' : undefined,
            borderRadius: theme.borderRadius.sm,
            mb: chip ? 0.5 : undefined,
            mr: chip ? 0.5 : undefined,
          } as any
        }
      >
        <HephaestusCheckbox
          checked={on}
          indeterminate={indeterminate}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setOnAndPropagate(event.target.checked);
          }}
          size={size}
          disabled={disabled}
          onClick={onClick}
          slotProps={{
            root: { 'data-testid': dataTestId },
            input: {
              id: inputId,
              name,
              'data-testid': `${dataTestId}-input`,
            },
          }}
          sx={
            {
              ...(error
                ? {
                    '& .hephaestus-checkbox-icon': {
                      borderColor: theme.color.negative,
                    },
                  }
                : {}),
              ...checkBoxStyle,
            } as any
          }
        />
        {label !== undefined && (
          <Typography
            component='label'
            htmlFor={inputId}
            variant={variant}
            sx={
              {
                color: !disabled
                  ? error
                    ? theme.color.negative
                    : labelColor
                  : disabledLabelColor,
                cursor: !disabled ? 'pointer' : 'default',
                ml: 0.5,
              } as any
            }
          >
            {label}
          </Typography>
        )}
        {infoTooltip && shouldDisplayInDialog && (
          <InfoDialog information={infoTooltip} center={centerInfoToolTip} />
        )}
      </Box>
      {infoTooltip && !shouldDisplayInDialog && (
        <HelpText text={infoTooltip} tone='neutral' />
      )}
    </Box>
  );
};
